import {Loader, Skeleton} from '@mantine/core';
import {MdArrowForward} from 'react-icons/md';
import type {Recruitment} from '../../../../core/entities/recruitment/schema';
import {CommonLink} from '../../../../shared/components/CommonLink';
import {LoginPromptOverlay} from '../../../../shared/components/LoginPromptOverlay';
import {useMediaQuery} from '../../../../shared/services/useMediaQuery';
import {CarouselRecruitmentCard} from './Presenter/CarouselRecruitmentCard';
import {SectionHeader} from './Presenter/SectionHeader';
import {CAROUSEL_CONFIG} from './constants';

type RecruitmentSectionBaseProps = {
  componentId: string;
  title: string;
  description?: string;
  href?: string;
  recruitments?: (Recruitment & {isViewed?: boolean})[];
  isLoading: boolean;
  limit?: number;
  isRanking?: boolean;
  isLogin?: boolean;
  isStudentVerificationRequired?: boolean;
  entryButtonLabel?: string;
  entryButtonTooltip?: string;
  entryButtonGradientClassName?: string;
};

// 「もっと見る」で追加読込する場合は onShowMore と hasMore をセットで要求し、
// 外部リンク（href）で遷移する場合はこれらを受け取らないことを型で保証する
type RecruitmentSectionShowMoreProps =
  | {
      onShowMore: () => void;
      hasMore: boolean;
      isFetchingMore?: boolean;
    }
  | {
      onShowMore?: undefined;
      hasMore?: undefined;
      isFetchingMore?: undefined;
    };

type RecruitmentSectionProps = RecruitmentSectionBaseProps &
  RecruitmentSectionShowMoreProps;

export const RecruitmentSection = ({
  componentId,
  title,
  description,
  recruitments,
  href,
  isLoading,
  isRanking = false,
  isLogin = true,
  isStudentVerificationRequired = false,
  limit = CAROUSEL_CONFIG.RECRUITMENT_LIMIT,
  onShowMore,
  hasMore,
  isFetchingMore = false,
  entryButtonLabel,
  entryButtonTooltip,
  entryButtonGradientClassName,
}: RecruitmentSectionProps) => {
  const {isPC} = useMediaQuery();

  // 大学認証未完了ユーザーは未ログインと同様にランキングを閲覧できない
  const isViewable = isLogin && !isStudentVerificationRequired;

  const renderSkeletons = () => {
    return Array.from({length: CAROUSEL_CONFIG.SKELETON_COUNT}).map(
      (_, index) => (
        <div
          key={`skeleton-${title}-${index}`}
          className={
            isPC
              ? 'whitespace-pre mt-16 flex-shrink-0'
              : 'rounded-16 flex-shrink-0 max-w-[320px]'
          }
        >
          <Skeleton height={220} width={isPC ? 369 : 320} />
        </div>
      ),
    );
  };

  const renderRecruitmentCards = () => {
    if (!recruitments) return null;

    return recruitments.map((recruitment, index) => {
      const cardContainerClass = isPC ? 'mt-16' : 'rounded-16 flex-shrink-0';

      return (
        <div key={recruitment.id} className={cardContainerClass}>
          <CarouselRecruitmentCard
            recruitment={recruitment}
            isViewed={recruitment.isViewed}
            rank={isRanking ? index + 1 : undefined}
            componentId={`${componentId}-item`}
            entryButtonLabel={entryButtonLabel}
            entryButtonTooltip={entryButtonTooltip}
            entryButtonGradientClassName={entryButtonGradientClassName}
          />
        </div>
      );
    });
  };

  if (!isLoading && !recruitments?.length) {
    return null;
  }

  const showMoreCardClass =
    'shadow-card bg-white p-16 rounded-16 hover:opacity-70 pc:mt-16 flex items-center flex-shrink-0';

  const ShowMoreCardContent = () => (
    <>
      <div className="flex justify-center py-16">
        {isFetchingMore ? <Loader size={40} /> : <MdArrowForward size={40} />}
      </div>
      <div>
        <div className="text-md text-black text-center w-[200px]">
          もっと見る
        </div>
      </div>
    </>
  );

  const ShowMoreCard = () => {
    if (onShowMore) {
      return (
        <button
          type="button"
          onClick={onShowMore}
          disabled={isFetchingMore}
          className={showMoreCardClass}
          data-gtm-click={`${componentId}-more`}
        >
          <ShowMoreCardContent />
        </button>
      );
    }

    return (
      <CommonLink
        href={href ?? ''}
        className={showMoreCardClass}
        data-gtm-click={`${componentId}-more`}
      >
        <ShowMoreCardContent />
      </CommonLink>
    );
  };

  return (
    <div id={componentId}>
      <SectionHeader
        componentId={componentId}
        title={title}
        href={href}
        description={description}
      />

      {isRanking && !isViewable ? (
        <LoginPromptOverlay
          title="募集情報ランキング"
          description="人気の募集情報をご覧いただくには会員登録が必要です。"
          studentVerificationDescription="人気の募集情報をご覧いただくには大学認証が必要です。"
          isStudentVerificationRequired={isStudentVerificationRequired}
        >
          <div className="flex overflow-x-scroll pc:overflow-auto gap-16 mt-16 pc:mt-0 pc:pb-16">
            {isLoading ? renderSkeletons() : renderRecruitmentCards()}
          </div>
        </LoginPromptOverlay>
      ) : (
        <div className="flex overflow-x-scroll pc:overflow-auto gap-16 mt-16 pc:mt-0 pc:pb-16">
          {isLoading ? renderSkeletons() : renderRecruitmentCards()}
          {onShowMore
            ? hasMore && <ShowMoreCard />
            : recruitments?.length === limit && href && <ShowMoreCard />}
        </div>
      )}
    </div>
  );
};
